/**
 * Note snapping for the tuner display.
 *
 * A string meter normally measures against the note the string is tuned to.
 * Once the player is more than half a semitone away, that reading stops being
 * useful — an E string pulled down to Eb should read "Eb, spot on", not
 * "E, 100 cents flat". These functions decide which note a bar displays and
 * what the deviation is relative to it.
 */

import { CENTS_PER_SEMITONE, centsOff, freqOf, nearestMidi } from './pitch'

/** Half a semitone: past this the neighbouring note is the better reference. */
export const SNAP_THRESHOLD_CENTS = CENTS_PER_SEMITONE / 2

/**
 * Extra margin a note must be pulled back through before the display releases
 * it again.
 *
 * A string sitting near the threshold — measured on a real guitar at about
 * -55 cents from its target — otherwise flips names on every small wobble,
 * because the switching point and the switching-back point are identical.
 * Widening the exit makes the display commit to a reading and stay there.
 */
export const SNAP_HYSTERESIS_CENTS = 12

/**
 * Slack for the threshold comparison.
 *
 * Round-tripping a frequency through `pow`/`log2` leaves error in the last
 * bits, so a value built to sit exactly on the threshold can land a hair above
 * it. Without this slack the behaviour at the boundary would depend on
 * rounding noise; 1e-6 cents is far below anything audible or displayable.
 */
const THRESHOLD_EPSILON_CENTS = 1e-6

/** What a single string meter should display. */
export interface SnappedReading {
  /** MIDI note the meter displays and measures against. */
  readonly midi: number
  /**
   * Deviation from {@link midi} in cents.
   *
   * Normally within [-50, +50]; while hysteresis is holding a note it can
   * reach [-62, +62]. The meter clamps its needle at the ends of the scale, so
   * the extra range shows as a pegged needle rather than an overshoot.
   */
  readonly cents: number
  /** The note the string is tuned to, regardless of snapping. */
  readonly targetMidi: number
  /** True when the displayed note differs from the string's target. */
  readonly isSnapped: boolean
  /** Signed distance from the target in semitones; 0 unless snapped. */
  readonly semitonesFromTarget: number
}

/**
 * Decide which note a string meter shows for a measured frequency.
 *
 * Within half a semitone of the string's target the target itself is used, so
 * the meter stays anchored while tuning. Beyond that the reading snaps to the
 * nearest equal-tempered note.
 *
 * Passing the note shown on the previous frame adds hysteresis: the display
 * keeps that note until the pitch moves a further
 * {@link SNAP_HYSTERESIS_CENTS} beyond the threshold, so a string parked near
 * the boundary does not flicker between two names.
 *
 * @param frequency - Measured fundamental in Hz; must be positive.
 * @param targetMidi - MIDI note the string is tuned to.
 * @param a4 - Reference pitch in Hz.
 * @param previousMidi - Note shown last frame, or null on the first frame.
 * @throws {RangeError} When the frequency is not a positive finite number.
 */
export function snapToNote(
  frequency: number,
  targetMidi: number,
  a4: number,
  previousMidi: number | null = null,
): SnappedReading {
  const centsToTarget = centsOff(frequency, freqOf(targetMidi, a4))

  if (Math.abs(centsToTarget) <= SNAP_THRESHOLD_CENTS + THRESHOLD_EPSILON_CENTS) {
    return {
      midi: targetMidi,
      cents: centsToTarget,
      targetMidi,
      isSnapped: false,
      semitonesFromTarget: 0,
    }
  }

  const nearest = nearestMidi(frequency, a4)

  // Stay on the note already being shown while the pitch is still within the
  // widened window around it, even though some other note is now marginally
  // closer.
  if (previousMidi !== null && previousMidi !== nearest.midi) {
    const centsToPrevious = centsOff(frequency, freqOf(previousMidi, a4))
    if (Math.abs(centsToPrevious) <= SNAP_THRESHOLD_CENTS + SNAP_HYSTERESIS_CENTS) {
      return {
        midi: previousMidi,
        cents: centsToPrevious,
        targetMidi,
        isSnapped: previousMidi !== targetMidi,
        semitonesFromTarget: previousMidi - targetMidi,
      }
    }
  }

  return {
    midi: nearest.midi,
    cents: nearest.cents,
    targetMidi,
    isSnapped: nearest.midi !== targetMidi,
    semitonesFromTarget: nearest.midi - targetMidi,
  }
}
